/**
 * Finite state machines over discriminated unions: states are told apart by `status`,
 * events by `type`. Every allowed transition is listed in the table. Any other pair of
 * state and event is a programming error and throws `IllegalStateTransitionError`, so it
 * never passes unnoticed (mirrors `server/src/dmw/kernel/state_machine.py`).
 */
interface MachineState {
  readonly status: string;
}

interface MachineEvent {
  readonly type: string;
}

type StateWithStatus<S extends MachineState, K extends S["status"]> = Extract<S, { status: K }>;
type EventWithType<E extends MachineEvent, K extends E["type"]> = Extract<E, { type: K }>;

export type TransitionTable<S extends MachineState, E extends MachineEvent> = {
  readonly [SK in S["status"]]: {
    readonly [EK in E["type"]]?: (
      state: StateWithStatus<S, SK>,
      event: EventWithType<E, EK>,
    ) => S;
  };
};

export class IllegalStateTransitionError extends Error {
  readonly machineName: string;
  readonly fromStatus: string;
  readonly eventType: string;

  constructor(machineName: string, fromStatus: string, eventType: string) {
    super(`${machineName}: event "${eventType}" is not allowed in state "${fromStatus}".`);
    this.name = "IllegalStateTransitionError";
    this.machineName = machineName;
    this.fromStatus = fromStatus;
    this.eventType = eventType;
  }
}

export type Transition<S extends MachineState, E extends MachineEvent> = (state: S, event: E) => S;

/** Build the transition function of a machine. Usable directly as a `useReducer` reducer. */
export function defineStateMachine<S extends MachineState, E extends MachineEvent>(
  machineName: string,
  table: TransitionTable<S, E>,
): Transition<S, E> {
  return (state, event) => {
    // The table is typed per state and event; looked up at run time, both are just strings.
    const transitionsFromState = table[state.status as S["status"]] as unknown as Partial<
      Record<string, (state: S, event: E) => S>
    >;
    const transition = transitionsFromState[event.type];
    if (transition === undefined) {
      throw new IllegalStateTransitionError(machineName, state.status, event.type);
    }
    return transition(state, event);
  };
}
